import { useEffect } from "react";
import { Box, Button, Group } from "@mantine/core";
import { spotlight } from "@mantine/spotlight";
import { useGo } from "../components/ConsoleShell";
import { Panel, Prompt, Screen, StatusDot, LoadError } from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { compact } from "../lib/vf";
import css from "./screens.module.css";

const TONE: Record<string, string> = {
  ok: "var(--vf-ok)",
  warn: "var(--vf-warn)",
  bad: "var(--vf-bad)",
  sec: "var(--vf-sec)",
};

export default function Dashboard() {
  const go = useGo();
  const openModal = useUiStore((s) => s.openModal);
  const openDetail = useUiStore((s) => s.openDetail);
  const workspaceId = useWorkspaceStore((s) => s.currentId);

  const health = useConsoleStore((s) => s.health);
  const loadError = useConsoleStore((s) => s.errors.health);
  const namespaces = useConsoleStore((s) => s.namespaces);
  const operations = useConsoleStore((s) => s.operations);
  const jobs = useConsoleStore((s) => s.jobs);
  const analytics = useConsoleStore((s) => s.analytics);

  const loadHealth = useConsoleStore((s) => s.loadHealth);
  const loadNamespaces = useConsoleStore((s) => s.loadNamespaces);
  const loadOperations = useConsoleStore((s) => s.loadOperations);
  const loadJobs = useConsoleStore((s) => s.loadJobs);
  const loadAnalytics = useConsoleStore((s) => s.loadAnalytics);

  useEffect(() => {
    if (!workspaceId) return;
    void loadHealth();
    void loadNamespaces();
    void loadOperations();
    void loadJobs();
    void loadAnalytics();
  }, [workspaceId, loadHealth, loadNamespaces, loadOperations, loadJobs, loadAnalytics]);

  const totalVectors = namespaces.reduce((sum, n) => sum + n.vectors, 0);
  const emptyWorkspace = namespaces.length === 0;

  const kpis = [
    { key: "vectors", value: totalVectors ? compact(totalVectors) : "0" },
    { key: "namespaces", value: String(namespaces.length) },
    { key: "searches · 24h", value: compact(analytics?.searchesTotal ?? 0) },
    { key: "p95", value: analytics?.p95Ms != null ? `${analytics.p95Ms}ms` : "—" },
  ];

  return (
    <Screen>
      <Prompt command="vf status" />
      <LoadError message={loadError} />

      {emptyWorkspace ? (
        <div className={css.onboarding}>
          <div className={css.onboardingTitle}>workspace ready — nothing indexed yet</div>
          <div className={css.onboardingBody}>
            Three steps to first results: create a namespace, upsert documents (or connect a repo),
            then query it.
          </div>
          <Group gap={10}>
            <Button size="compact-sm" fz="sm" onClick={() => go("/namespaces/new")}>
              ▸ create namespace
            </Button>
            <Button
              size="compact-sm"
              fz="sm"
              variant="default"
              onClick={() => openModal("connectRepo")}
            >
              connect a repo
            </Button>
            <Button size="compact-sm" fz="sm" variant="default" onClick={() => go("/keys")}>
              get an API key
            </Button>
          </Group>
        </div>
      ) : null}

      <Box className={css.tiles4} mb={14}>
        {health.map((h) => (
          <div key={h.key} className={css.healthTile}>
            <div className={css.healthKey}>{h.key}</div>
            <div className={css.healthValue} style={{ color: TONE[h.tone] }}>
              ● {h.value}
            </div>
            <div className={css.healthDetail}>{h.detail}</div>
          </div>
        ))}
      </Box>

      <Box className={css.tiles4} mb={20}>
        {kpis.map((k) => (
          <div key={k.key} className={css.kpiTile}>
            <span className={css.kpiKey}>{k.key}</span>
            <span className={css.kpiValue}>{k.value}</span>
          </div>
        ))}
      </Box>

      <Box className={css.split2} mb={20}>
        <Panel title="recent operations">
          {operations.slice(0, 4).map((o) => (
            <button
              key={o.id}
              type="button"
              className={css.listRow}
              onClick={() => openDetail("operation", o.id)}
            >
              <span style={{ color: "var(--vf-acc)", width: 96, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {o.id}
              </span>
              <span style={{ color: "var(--vf-sec)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {o.kind} · {o.namespace}
              </span>
              <StatusDot status={o.status} />
            </button>
          ))}
        </Panel>

        <Panel title="recent jobs">
          {jobs.slice(0, 4).map((j) => (
            <button
              key={j.id}
              type="button"
              className={css.listRow}
              onClick={() => openDetail("job", j.id)}
            >
              <span style={{ color: "var(--vf-acc)", width: 64 }}>{j.kind}</span>
              <span style={{ color: "var(--vf-sec)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {j.namespace}
              </span>
              <StatusDot status={j.status} />
            </button>
          ))}
        </Panel>
      </Box>

      <div className={css.sectionNote}>// quick actions</div>
      <Group gap={10}>
        <QuickAction label="+ create namespace" onClick={() => go("/namespaces/new")} />
        <QuickAction label="▸ run a query" onClick={() => go("/query")} />
        <QuickAction label="＋ new api key" onClick={() => openModal("createKey")} />
        <QuickAction label="⌘K command palette" onClick={spotlight.open} muted />
      </Group>
    </Screen>
  );
}

function QuickAction({
  label,
  onClick,
  muted,
}: {
  label: string;
  onClick: () => void;
  muted?: boolean;
}) {
  return (
    <Button
      variant="light"
      size="compact-sm"
      onClick={onClick}
      styles={{
        root: {
          background: "var(--vf-accbg)",
          border: "1px solid var(--vf-border)",
          color: muted ? "var(--vf-sec)" : "var(--vf-acc)",
          padding: "9px 15px",
          height: "auto",
          fontSize: "12.5px",
        },
      }}
    >
      {label}
    </Button>
  );
}
